import { ACCENTS_TOKENS, NOMS_ACCENTS, THEMES_TOKENS } from './palette'

/**
 * Les trois thèmes et les six accents — 06 §3.
 *
 * RG-D3 : les trois thèmes, plus « suivre le système ».
 * RG-D4 : le thème Noir n'est pas un détail — sur une dalle OLED d'entrée de
 * gamme, il réduit sensiblement la consommation. C'est un argument d'usage
 * réel en Haïti, pas une préférence esthétique.
 *
 * Les valeurs viennent de `palette.ts`, GÉNÉRÉ depuis tokens.json. Ce fichier
 * les redéclarait autrefois : la moindre retouche de palette laissait un
 * accent juste dans le CSS et faux en JavaScript.
 */
export const THEMES = ['system', 'light', 'dim', 'lights-out'] as const
export type Theme = (typeof THEMES)[number]

export type Accent = (typeof NOMS_ACCENTS)[number]

/**
 * La couleur d'IDENTITÉ de chaque accent — celle de la pastille de choix.
 *
 * L'accent réellement peint est ajusté au fond du thème courant (`--accent`,
 * généré). Ici on montre la couleur telle qu'elle se nomme : « vert » doit
 * ressembler à du vert dans le sélecteur, quel que soit le thème actif.
 */
export const ACCENTS: Record<Accent, string> = Object.fromEntries(
  NOMS_ACCENTS.map((nom) => [nom, ACCENTS_TOKENS.light[nom].base]),
) as Record<Accent, string>

export const THEME_LABELS: Record<Theme, string> = {
  system: 'Suivre le système',
  light: THEMES_TOKENS.light.label,
  dim: THEMES_TOKENS.dim.label,
  'lights-out': THEMES_TOKENS['lights-out'].label,
}

/** Le thème effectif : `system` se résout sur la préférence du navigateur. */
export function resolveTheme(theme: Theme): Exclude<Theme, 'system'> {
  if (theme !== 'system') return theme

  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dim' : 'light'
}

/**
 * Applique thème et accent par ATTRIBUTS, pas par variables posées à la main.
 *
 * Poser `--accent` en style inline ne poserait que celle-là : les surfaces qui
 * portent du texte ont besoin de `--accent-fort` et `--sur-accent`, calculées
 * pour tenir 4,5:1 (RG-D1). Un seul attribut amène les trois, cohérentes.
 */
export function applyTheme(theme: Theme, accent: Accent): void {
  const root = document.documentElement

  root.dataset['theme'] = resolveTheme(theme)
  root.dataset['accent'] = accent
}
